import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import FormCard from './components/FormCard';

import ActivityFeed from './components/ActivityFeed';
import EmptyState from './components/EmptyState';
import FolderManager from './components/FolderManager';

import MoveToFolderModal from './components/MoveToFolderModal';
import ShareFormModal from './components/ShareFormModal';
import OnboardingWalkthrough from '../../components/ui/OnboardingWalkthrough';
import { formsService } from '../../services/formsService';
import { foldersService } from '../../services/foldersService';
import { submissionsService } from '../../services/submissionsService';
import { useAuth } from '../../contexts/AuthContext';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { formatLimit } from '../../config/planFeatures';

const Modal = ({ isOpen, onClose, title = '', children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-heading font-semibold text-foreground">{title}</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  const { canCreateForm, isAtFormLimit, limits, planType: creatorPlanType, loading: planLoading } = usePlanLimits();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('modified');
  const [selectedForms, setSelectedForms] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState([1, 2, 3]);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [folderError, setFolderError] = useState('');
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [teamMembersCount, setTeamMembersCount] = useState(3);
  const [activityPosition, setActivityPosition] = useState('top'); // 'top' or 'bottom'
  const [showViewFoldersModal, setShowViewFoldersModal] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [shareForm, setShareForm] = useState(null);
  const [publishingFormId, setPublishingFormId] = useState(null);
  const [showNewFormModal, setShowNewFormModal] = useState(false);
  const [showUpgradeLimitModal, setShowUpgradeLimitModal] = useState(false);

  // Drag-and-drop state
  const [formOrder, setFormOrder] = useState([]);
  const dragItemId = useRef(null);
  const dragOverItemId = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Load saved order from localStorage
  useEffect(() => {
    if (user) {
      const saved = localStorage.getItem(`formOrder_${user?.id}`);
      if (saved) {
        try {
          setFormOrder(JSON.parse(saved));
        } catch (e) {
          setFormOrder([]);
        }
      }
    }
  }, [user]);

  // Save order to localStorage whenever it changes
  const saveFormOrder = (order) => {
    if (user) {
      localStorage.setItem(`formOrder_${user?.id}`, JSON.stringify(order));
    }
    setFormOrder(order);
  };

  const handleDragStart = (e, formId) => {
    dragItemId.current = formId;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, formId) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    dragOverItemId.current = formId;
    setDragOverId(formId);
  };

  const handleDragEnd = () => {
    dragItemId.current = null;
    dragOverItemId.current = null;
    setDragOverId(null);
  };

  const handleDrop = (e, targetId) => {
    e?.preventDefault();
    const sourceId = dragItemId?.current;
    if (!sourceId || sourceId === targetId) {
      handleDragEnd();
      return;
    }

    // Build current ordered list of all form IDs
    const orderedIds = getOrderedForms(forms)?.map(f => f?.id);
    const sourceIdx = orderedIds?.indexOf(sourceId);
    const targetIdx = orderedIds?.indexOf(targetId);

    if (sourceIdx === -1 || targetIdx === -1) {
      handleDragEnd();
      return;
    }

    const newOrder = [...orderedIds];
    newOrder?.splice(sourceIdx, 1);
    newOrder?.splice(targetIdx, 0, sourceId);

    saveFormOrder(newOrder);
    handleDragEnd();
  };

  // Apply saved order to a list of forms
  const getOrderedForms = (formsList) => {
    if (!formOrder || formOrder?.length === 0) return formsList;
    const orderMap = {};
    formOrder?.forEach((id, idx) => { orderMap[id] = idx; });
    return [...formsList]?.sort((a, b) => {
      const aIdx = orderMap?.[a?.id] !== undefined ? orderMap?.[a?.id] : Infinity;
      const bIdx = orderMap?.[b?.id] !== undefined ? orderMap?.[b?.id] : Infinity;
      return aIdx - bIdx;
    });
  };

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/authentication');
      return;
    }

    loadData();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;

    // Subscribe to real-time updates
    const unsubscribeForms = formsService?.subscribeToForms(user?.id, (payload) => {
      if (payload?.eventType === 'INSERT') {
        setForms(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setForms(prev => prev?.map(f => f?.id === payload?.new?.id ? payload?.new : f));
      } else if (payload?.eventType === 'DELETE') {
        setForms(prev => prev?.filter(f => f?.id !== payload?.old?.id));
      }
    });

    const unsubscribeFolders = foldersService?.subscribeToFolders(user?.id, (payload) => {
      if (payload?.eventType === 'INSERT') {
        setFolders(prev => [...prev, payload?.new]);
      } else if (payload?.eventType === 'UPDATE') {
        setFolders(prev => prev?.map(f => f?.id === payload?.new?.id ? payload?.new : f));
      } else if (payload?.eventType === 'DELETE') {
        setFolders(prev => prev?.filter(f => f?.id !== payload?.old?.id));
      }
    });

    return () => {
      unsubscribeForms();
      unsubscribeFolders();
    };
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [formsData, foldersData, submissionsData] = await Promise.all([
        formsService?.getForms(),
        foldersService?.getFolders(),
        submissionsService?.getAllSubmissions()
      ]);
      setForms(formsData);
      setFolders(foldersData);
      setTotalSubmissions(submissionsData?.length || 0);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const mockStats = [
    {
      id: 1,
      type: 'forms',
      icon: 'FileText',
      label: 'Total Forms',
      value: 24,
      trend: 12,
      limit: 50
    },
    {
      id: 2,
      type: 'submissions',
      icon: 'Send',
      label: 'Monthly Submissions',
      value: 3557,
      trend: 8,
      limit: 5000
    },
    {
      id: 3,
      type: 'views',
      icon: 'Eye',
      label: 'Form Views',
      value: 12843,
      trend: -3,
      limit: null
    }
  ];

  const mockActivities = [
    {
      id: 1,
      type: 'submission',
      description: 'New submission received',
      formName: 'Contact Us Form',
      formId: forms?.[0]?.id || null,
      timestamp: new Date('2026-01-30T06:30:00'),
      count: 3
    },
    {
      id: 2,
      type: 'edit',
      description: 'Form updated',
      formName: 'Product Feedback Form',
      formId: forms?.[1]?.id || null,
      timestamp: new Date('2026-01-30T05:45:00'),
      count: null
    },
    {
      id: 3,
      type: 'publish',
      description: 'Form published',
      formName: 'Event Registration',
      formId: forms?.[2]?.id || null,
      timestamp: new Date('2026-01-29T18:20:00'),
      count: null
    },
    {
      id: 5,
      type: 'duplicate',
      description: 'Form duplicated',
      formName: 'Appointment Booking',
      formId: forms?.[3]?.id || null,
      timestamp: new Date('2026-01-29T14:30:00'),
      count: null
    }
  ];

  const planData = {
    planName: 'Professional',
    billingCycle: 'Jan 15 - Feb 15, 2026',
    nextBillingDate: 'February 15, 2026',
    monthlyPrice: 49,
    limits: [
      {
        id: 1,
        icon: 'FileText',
        label: 'Forms',
        used: forms?.length || 0,
        limit: 50
      },
      {
        id: 2,
        icon: 'Send',
        label: 'Monthly Submissions',
        used: totalSubmissions,
        limit: 5000
      },
      {
        id: 3,
        icon: 'Users',
        label: 'Team Members',
        used: teamMembersCount,
        limit: 5
      }
    ]
  };

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'published', label: 'Published' },
    { value: 'draft', label: 'Draft' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'Contact', label: 'Contact' },
    { value: 'Survey', label: 'Survey' },
    { value: 'Booking', label: 'Booking' },
    { value: 'Feedback', label: 'Feedback' },
    { value: 'Registration', label: 'Registration' },
    { value: 'Onboarding', label: 'Onboarding' }
  ];

  const sortOptions = [
    { value: 'modified', label: 'Last Modified' },
    { value: 'name', label: 'Name' },
    { value: 'submissions', label: 'Submissions' },
    { value: 'created', label: 'Date Created' }
  ];

  const filteredForms = getOrderedForms(
    forms?.filter(form => {
      const matchesSearch = form?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      const matchesStatus = filterStatus === 'all' || form?.status === filterStatus;
      const matchesType = filterType === 'all' || form?.type === filterType;
      const matchesFolder = selectedFolder === null || form?.folderId === selectedFolder;
      return matchesSearch && matchesStatus && matchesType && matchesFolder;
    })
  );

  const handleSelectForm = (formId) => {
    setSelectedForms((prev) => {
      const newSelection = prev?.includes(formId) ?
      prev?.filter((id) => id !== formId) :
      [...prev, formId];
      setShowBulkActions(newSelection?.length > 0);
      if (newSelection?.length === 0) setActionError(null);
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    if (selectedForms?.length === filteredForms?.length) {
      setSelectedForms([]);
      setShowBulkActions(false);
      setActionError(null);
    } else {
      setSelectedForms(filteredForms?.map((form) => form?.id));
      setShowBulkActions(true);
    }
  };

  const handleDuplicate = async (formId) => {
    // Check plan limit before duplicating
    if (!canCreateForm()) {
      setShowUpgradeLimitModal(true);
      return;
    }
    try {
      const duplicated = await formsService?.duplicateForm(formId);
      if (duplicated) {
        setForms(prev => [duplicated, ...prev]);
      }
    } catch (err) {
      console.error('Error duplicating form:', err);
      setError('Failed to duplicate form');
    }
  };

  const handleArchive = async (formId) => {
    try {
      const success = await formsService?.deleteForm(formId);
      if (success) {
        setForms(prev => prev?.filter(f => f?.id !== formId));
      }
    } catch (err) {
      console.error('Error archiving form:', err);
      setError('Failed to archive form');
    }
  };

  const handleShare = (formId) => {
    const form = forms?.find(f => f?.id === formId);
    if (form) setShareForm(form);
  };

  const handlePublish = async (formId, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    setPublishingFormId(formId);
    try {
      const updated = await formsService?.updateForm(formId, { status: newStatus });
      if (updated) {
        setForms(prev => prev?.map(f => f?.id === formId ? { ...f, status: newStatus } : f));
      }
    } catch (err) {
      console.error('Error toggling publish status:', err);
      setError('Failed to update form status');
    } finally {
      setPublishingFormId(null);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedForms?.length === 0) return;
    
    setIsDeleting(true);
    try {
      // Delete all selected forms, tracking individual results
      const results = await Promise.allSettled(
        selectedForms?.map(formId => formsService?.deleteForm(formId).then(ok => ({ formId, ok })))
      );

      const succeeded = results
        .filter(r => r.status === 'fulfilled' && r.value?.ok)
        .map(r => r.value.formId);
      const failedCount = selectedForms?.length - succeeded.length;

      // Remove only the forms that were successfully deleted
      if (succeeded.length > 0) {
        setForms(prev => prev?.filter(f => !succeeded?.includes(f?.id)));
      }

      if (failedCount > 0) {
        // Keep failed forms selected so the creator can retry
        setSelectedForms(prev => prev?.filter(id => !succeeded?.includes(id)));
        setActionError(
          failedCount === selectedForms?.length
            ? `Failed to delete ${failedCount} form${failedCount === 1 ? '' : 's'}. Please try again.`
            : `${succeeded.length} form${succeeded.length === 1 ? '' : 's'} deleted, but ${failedCount} failed. The failed form${failedCount === 1 ? '' : 's'} remain${failedCount === 1 ? 's' : ''} selected so you can retry.`
        );
        setShowDeleteSelectedModal(false);
      } else {
        setActionError(null);
        setSelectedForms([]);
        setShowBulkActions(false);
        setShowDeleteSelectedModal(false);
      }
    } catch (err) {
      console.error('Error deleting forms:', err);
      setActionError('Failed to delete selected forms. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateFolder = async (name) => {
    try {
      const newFolder = await foldersService?.createFolder(name);
      if (newFolder) {
        setFolders(prev => [...prev, newFolder]);
      }
    } catch (err) {
      console.error('Error creating folder:', err);
      setError('Failed to create folder');
    }
  };

  const handleRenameFolder = async (folderId, newName) => {
    try {
      const updated = await foldersService?.renameFolder(folderId, newName);
      if (updated) {
        setFolders(prev => prev?.map(f => f?.id === folderId ? updated : f));
      }
    } catch (err) {
      console.error('Error renaming folder:', err);
      setError('Failed to rename folder');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      const success = await foldersService?.deleteFolder(folderId);
      if (success) {
        setFolders(prev => prev?.filter(f => f?.id !== folderId));
        setForms(prev => prev?.map(f => f?.folderId === folderId ? { ...f, folderId: null } : f));
        if (selectedFolder === folderId) {
          setSelectedFolder(null);
        }
      }
    } catch (err) {
      console.error('Error deleting folder:', err);
      setError('Failed to delete folder');
    }
  };

  const handleMoveToFolder = async (formIds, folderId) => {
    try {
      const success = await foldersService?.moveFormsToFolder(formIds, folderId);
      if (success) {
        setForms(prev => prev?.map(f => 
          formIds?.includes(f?.id) ? { ...f, folderId } : f
        ));
        setShowMoveModal(false);
        setSelectedForms([]);
        setShowBulkActions(false);
      } else {
        // Move failed — close modal but keep forms selected so the creator can retry
        setShowMoveModal(false);
        setActionError(
          `Failed to move ${formIds?.length} form${formIds?.length === 1 ? '' : 's'}. The selection has been kept so you can try again.`
        );
      }
    } catch (err) {
      console.error('Error moving forms:', err);
      // Close modal but keep forms selected so the creator can retry
      setShowMoveModal(false);
      setActionError(
        `Failed to move ${formIds?.length} form${formIds?.length === 1 ? '' : 's'}. The selection has been kept so you can try again.`
      );
    }
  };

  const handleSelectFolder = (folderId) => {
    setSelectedFolder(folderId);
    setSelectedForms([]);
    setShowBulkActions(false);
  };

  const handleToggleFolder = (folderId) => {
    setExpandedFolders(prev => 
      prev?.includes(folderId) 
        ? prev?.filter(id => id !== folderId)
        : [...prev, folderId]
    );
  };

  const getFormCountByFolder = (folderId) => {
    if (folderId === null) {
      return forms?.length;
    }
    return forms?.filter(f => f?.folderId === folderId)?.length;
  };

  const openRenameFolderModal = (folder) => {
    setFolderToEdit(folder);
    setRenameFolderName(folder?.name);
    setFolderError('');
    setShowRenameFolderModal(true);
  };

  const openDeleteFolderModal = (folder) => {
    setFolderToEdit(folder);
    setShowDeleteFolderModal(true);
  };

  const handleRenameFolderSubmit = () => {
    if (!renameFolderName?.trim()) {
      setFolderError('Folder name is required');
      return;
    }

    const exists = folders?.some(f => 
      f?.id !== folderToEdit?.id && 
      f?.name?.toLowerCase() === renameFolderName?.trim()?.toLowerCase()
    );
    if (exists) {
      setFolderError('A folder with this name already exists');
      return;
    }

    handleRenameFolder(folderToEdit?.id, renameFolderName?.trim());
    setRenameFolderName('');
    setFolderError('');
    setShowRenameFolderModal(false);
    setFolderToEdit(null);
  };

  const handleDeleteFolderSubmit = () => {
    handleDeleteFolder(folderToEdit?.id);
    setShowDeleteFolderModal(false);
    setFolderToEdit(null);
  };

  const handleCreateNewForm = () => {
    if (!canCreateForm()) {
      setShowUpgradeLimitModal(true);
      return;
    }
    setShowNewFormModal(true);
  };

  const stats = [
    {
      label: 'Total Forms',
      value: forms?.length,
      change: '+12%',
      trend: 'up',
      icon: 'FileText'
    },
    {
      label: 'Total Submissions',
      value: forms?.reduce((sum, form) => sum + (form?.submissionsCount || 0), 0),
      change: '+23%',
      trend: 'up',
      icon: 'Send'
    },
    {
      label: 'Published Forms',
      value: forms?.filter(f => f?.status === 'published')?.length,
      change: '+8%',
      trend: 'up',
      icon: 'CheckCircle2'
    }
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <p className="text-foreground font-medium mb-2">Failed to load dashboard</p>
          <p className="text-muted-foreground text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <NavigationBar stats={stats}>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" data-tour="dashboard-header">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
                Dashboard
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Manage your forms and track performance
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowWalkthrough(true)}
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border hover:border-primary/40 rounded-lg px-3 py-2 transition-all duration-200 hover:bg-muted"
                title="Take a guided tour"
              >
                <Icon name="MapPin" size={15} color="var(--color-primary)" />
                <span>Take Tour</span>
              </button>
              <div data-tour="create-form-btn">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleCreateNewForm}
                >
                  Create New Form
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="p-4 md:p-5 lg:p-6 border-b border-border">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {filteredForms?.length > 0 && (
                        <button
                          onClick={handleSelectAll}
                          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth flex-shrink-0"
                        >
                          <div className={`
                            w-5 h-5 rounded border-2 transition-smooth
                            ${selectedForms?.length === filteredForms?.length ?
                              'bg-primary border-primary' : 'border-border hover:border-primary'}
                          `}>
                            {selectedForms?.length === filteredForms?.length && (
                              <Icon name="Check" size={12} color="white" className="mx-auto mt-0.5" />
                            )}
                          </div>
                          <span>
                            {selectedForms?.length === filteredForms?.length ?
                              'Deselect All' : 'Select All'
                            }
                          </span>
                        </button>
                      )}
                      <div className="flex-1 min-w-0">
                        <Input
                          type="search"
                          placeholder="Search forms..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e?.target?.value)}
                          className="w-full"
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <FolderManager
                        folders={folders}
                        onCreateFolder={handleCreateFolder}
                        onRenameFolder={handleRenameFolder}
                        onDeleteFolder={handleDeleteFolder}
                      />

                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Folder"
                        iconPosition="left"
                        onClick={() => setShowViewFoldersModal(true)}
                      >
                        View Folders
                      </Button>

                      <Select
                        options={statusOptions}
                        value={filterStatus}
                        onChange={setFilterStatus}
                        placeholder="Status"
                        className="w-full sm:w-auto"
                      />

                      <Select
                        options={typeOptions}
                        value={filterType}
                        onChange={setFilterType}
                        placeholder="Type"
                        className="w-full sm:w-auto"
                      />

                      <Select
                        options={sortOptions}
                        value={sortBy}
                        onChange={setSortBy}
                        placeholder="Sort by"
                        className="w-full sm:w-auto"
                      />
                    </div>
                  </div>

                  {showBulkActions && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-muted-foreground">
                          {selectedForms?.length} selected
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="FolderInput"
                          iconPosition="left"
                          onClick={() => { setActionError(null); setShowMoveModal(true); }}
                        >
                          Move to Folder
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          iconName="Trash2"
                          iconPosition="left"
                          onClick={() => { setActionError(null); setShowDeleteSelectedModal(true); }}
                        >
                          Delete Selected
                        </Button>
                      </div>
                      {actionError && (
                        <div className="flex items-start gap-2 mt-3 p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                          <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" />
                          <span>{actionError}</span>
                          <button
                            onClick={() => setActionError(null)}
                            className="ml-auto flex-shrink-0 hover:opacity-70 transition-opacity"
                            aria-label="Dismiss error"
                          >
                            <Icon name="X" size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Folder Tabs */}
                {(folders?.length > 0) && (
                  <div className="px-4 md:px-5 lg:px-6 border-b border-border bg-muted/30">
                    <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                      {/* All Forms tab */}
                      <button
                        onClick={() => handleSelectFolder(null)}
                        className={`
                          flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-150 flex-shrink-0
                          ${selectedFolder === null
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }
                        `}
                      >
                        <Icon name="LayoutGrid" size={13} />
                        <span>All Forms</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${selectedFolder === null ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                          {forms?.length}
                        </span>
                      </button>

                      {/* Divider */}
                      <div className="w-px h-5 bg-border flex-shrink-0 mx-1" />

                      {/* Folder tabs */}
                      {folders?.map((folder) => {
                        const count = forms?.filter(f => f?.folderId === folder?.id)?.length;
                        const isActive = selectedFolder === folder?.id;
                        return (
                          <button
                            key={folder?.id}
                            onClick={() => handleSelectFolder(isActive ? null : folder?.id)}
                            className={`
                              flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-150 flex-shrink-0
                              ${isActive
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                              }
                            `}
                          >
                            <Icon name="Folder" size={13} />
                            <span className={isActive ? '' : ''}  style={isActive ? {} : {color: '#0e7572'}}>{folder?.name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                              {count}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Forms Horizontal Scroll */}
                <div className="p-3 md:p-4">
                  {filteredForms?.length > 0 ? (
                    <div className="space-y-1.5">
                      {filteredForms?.map((form) => (
                        <div key={form?.id} className="w-full">
                          <FormCard
                            form={form}
                            onDuplicate={handleDuplicate}
                            onArchive={handleArchive}
                            onShare={handleShare}
                            onPublish={handlePublish}
                            isSelected={selectedForms?.includes(form?.id)}
                            onSelect={handleSelectForm}
                            onMoveToFolder={(formIds) => {
                              setSelectedForms(formIds);
                              setShowMoveModal(true);
                            }}
                            isListView={true}
                            draggable={true}
                            onDragStart={(e) => handleDragStart(e, form?.id)}
                            onDragOver={(e) => handleDragOver(e, form?.id)}
                            onDragEnd={handleDragEnd}
                            onDrop={(e) => handleDrop(e, form?.id)}
                            isDragOver={dragOverId === form?.id}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState type={searchQuery ? 'search' : 'forms'} />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity - Moved below forms section */}
          <div className="mt-6">
            <ActivityFeed
              activities={mockActivities}
              position={activityPosition}
              onMoveToTop={() => setActivityPosition('top')}
              onMoveToBottom={() => setActivityPosition('bottom')}
              onMoveUp={() => {}}
              onMoveDown={() => {}}
              showMoveUp={false}
              showMoveDown={false}
            />
          </div>
        </div>
      </NavigationBar>

      {/* Share Form Modal */}
      <ShareFormModal
        isOpen={!!shareForm}
        form={shareForm}
        onClose={() => setShareForm(null)}
      />

      {/* Move to Folder Modal */}
      <MoveToFolderModal
        isOpen={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        folders={folders}
        selectedForms={selectedForms}
        onMoveToFolder={handleMoveToFolder}
        forms={forms}
      />

      {/* Rename Folder Modal */}
      <Modal
        isOpen={showRenameFolderModal}
        onClose={() => setShowRenameFolderModal(false)}
        title="Rename Folder"
      >
        <div className="space-y-4">
          <Input
            label="Folder Name"
            placeholder="Enter new folder name"
            value={renameFolderName}
            onChange={(e) => {
              setRenameFolderName(e?.target?.value);
              setFolderError('');
            }}
            error={folderError}
            autoFocus
          />
          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowRenameFolderModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleRenameFolderSubmit}
            >
              Rename
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Folder Modal */}
      <Modal
        isOpen={showDeleteFolderModal}
        onClose={() => setShowDeleteFolderModal(false)}
        title="Delete Folder"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this folder? Forms inside will not be deleted.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteFolderModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteFolderSubmit}
            >
              Delete Folder
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Selected Forms Confirmation Modal */}
      <Modal
        isOpen={showDeleteSelectedModal}
        onClose={() => setShowDeleteSelectedModal(false)}
        title="Delete Forms"
      >
        <div className="space-y-4">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-destructive mt-0.5" />
              <div>
                <h4 className="font-semibold text-destructive mb-1">Warning: Permanent Action</h4>
                <p className="text-sm text-muted-foreground">
                  You are about to permanently delete <strong>{selectedForms?.length}</strong> form{selectedForms?.length !== 1 ? 's' : ''}. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            All submissions, analytics, and settings associated with {selectedForms?.length === 1 ? 'this form' : 'these forms'} will also be permanently deleted.
          </p>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteSelectedModal(false);
                setSelectedForms([]);
                setShowBulkActions(false);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkArchive}
              disabled={isDeleting}
              iconName={isDeleting ? 'Loader2' : 'Trash2'}
              iconPosition="left"
            >
              {isDeleting ? 'Deleting...' : `Delete ${selectedForms?.length} Form${selectedForms?.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Folders Modal */}
      <Modal
        isOpen={showViewFoldersModal}
        onClose={() => setShowViewFoldersModal(false)}
        title="All Folders"
      >
        <div className="space-y-4">
          {folders?.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground mb-3">
                You have {folders?.length} folder{folders?.length !== 1 ? 's' : ''}
              </p>
              <div className="max-h-96 overflow-y-auto space-y-2">
                {folders?.map((folder) => {
                  const formCount = getFormCountByFolder(folder?.id);
                  return (
                    <div
                      key={folder?.id}
                      className="flex items-center justify-between p-3 bg-muted/50 hover:bg-muted rounded-lg transition-smooth cursor-pointer"
                      onClick={() => {
                        handleSelectFolder(folder?.id);
                        setShowViewFoldersModal(false);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon name="Folder" size={20} className="text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground">{folder?.name}</h4>
                          <p className="text-xs text-muted-foreground">
                            {formCount} form{formCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            openRenameFolderModal(folder);
                            setShowViewFoldersModal(false);
                          }}
                          className="p-2 hover:bg-background rounded-md transition-smooth"
                          title="Rename folder"
                        >
                          <Icon name="Edit2" size={16} className="text-muted-foreground hover:text-foreground" />
                        </button>
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            openDeleteFolderModal(folder);
                            setShowViewFoldersModal(false);
                          }}
                          className="p-2 hover:bg-background rounded-md transition-smooth"
                          title="Delete folder"
                        >
                          <Icon name="Trash2" size={16} className="text-destructive" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Icon name="FolderOpen" size={32} className="text-muted-foreground" />
              </div>
              <h4 className="font-medium text-foreground mb-2">No folders yet</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first folder to organize your forms
              </p>
              <Button
                variant="default"
                size="sm"
                iconName="FolderPlus"
                iconPosition="left"
                onClick={() => {
                  setShowViewFoldersModal(false);
                  // Trigger the New Folder button in FolderManager
                }}
              >
                Create Folder
              </Button>
            </div>
          )}
        </div>
      </Modal>

      {/* Upgrade Limit Modal */}
      {showUpgradeLimitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setShowUpgradeLimitModal(false)} />
          <div className="relative bg-card border border-border rounded-xl shadow-elevation-lg w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground">Form Limit Reached</h3>
              </div>
              <button
                onClick={() => setShowUpgradeLimitModal(false)}
                className="text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="bg-error/5 border border-error/20 rounded-lg p-4 mb-5">
              <p className="text-sm text-foreground leading-relaxed">
                You've used all{' '}
                <strong>{formatLimit(limits.maxForms)} form{limits.maxForms === 1 ? '' : 's'}</strong>{' '}
                on your{' '}
                <strong className="capitalize">{creatorPlanType}</strong> plan.
                Delete an existing form or upgrade to create more.
              </p>
            </div>

            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              Upgrade your account to unlock more forms, advanced analytics, payment integrations, and much more.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Button
                variant="default"
                className="w-full sm:w-auto"
                iconName="Zap"
                iconPosition="left"
                onClick={() => {
                  setShowUpgradeLimitModal(false);
                  navigate('/account-settings?tab=billing');
                }}
              >
                Upgrade Plan
              </Button>
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => setShowUpgradeLimitModal(false)}
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* New Form Choice Modal */}
      {showNewFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setShowNewFormModal(false)} />
          <div className="relative bg-card border border-border rounded-xl shadow-elevation-lg w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xl font-heading font-semibold text-foreground">Create a New Form</h3>
              <button
                onClick={() => setShowNewFormModal(false)}
                className="text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-6">How would you like to get started?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Use a Template */}
              <button
                onClick={() => { setShowNewFormModal(false); navigate('/templates-gallery'); }}
                className="group flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-border hover:border-primary bg-muted/30 hover:bg-primary/5 transition-all duration-200 text-left"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Icon name="LayoutTemplate" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="font-semibold text-foreground text-sm mb-1">Use a Template</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">Browse ready-made templates and customize to your needs</p>
                </div>
              </button>

              {/* Start from Scratch */}
              <button
                onClick={() => { setShowNewFormModal(false); navigate('/form-builder?new=1'); }}
                className="group flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-border hover:border-primary bg-muted/30 hover:bg-primary/5 transition-all duration-200 text-left"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Icon name="PenLine" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="font-semibold text-foreground text-sm mb-1">Start from Scratch</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">Build a fully custom form from a blank canvas</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Walkthrough */}
      <OnboardingWalkthrough
        forceShow={showWalkthrough}
        onComplete={() => setShowWalkthrough(false)}
      />
    </div>
  );
};

export default Dashboard;